import { useEffect, useRef } from 'react';
import { resolveShortcut, type ShortcutAction } from '@/lib/keyboard-shortcuts';

export type ShortcutHandlers = Record<ShortcutAction, () => void>;

/**
 * One document-level keydown listener for the global shortcuts (sprint 9). Handlers are read through a
 * ref so the listener is attached once per `enabled` change, not on every render.
 */
export function useGlobalShortcuts(handlers: ShortcutHandlers, enabled: boolean): void {
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      const action = resolveShortcut(event, document);
      if (action === null) return;
      // Stops the letter reaching the input the opened dialog autofocuses.
      event.preventDefault();
      handlersRef.current[action]();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
